import React, { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Layers3 } from "lucide-react";
import { Link } from "react-router-dom";
import { getErrorMessage, getImageUrl } from "../../core/api.jsx";
import { getCategories } from "../../core/services/catalogService";

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    getCategories()
      .then((data) => {
        if (alive) setCategories(data.categories || []);
      })
      .catch((requestError) => {
        if (alive)
          setError(getErrorMessage(requestError, "We couldn't load categories."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [attempt]);

  return (
    <main className="page-shell content-page categories-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Categories</span>
      </div>

      <section className="categories-page__intro">
        <p className="eyebrow"><Layers3 size={14} /> BROWSE THE STORE</p>
        <h1>Explore all <em>categories.</em></h1>
        <p>Find what you need by category, then browse its subcategories.</p>
      </section>

      {error && (
        <div className="inline-alert">
          <span>{error}</span>
          <button className="text-button" onClick={() => {
            setLoading(true);
            setError("");
            setAttempt((current) => current + 1);
          }}>
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <div className="categories-page__grid" aria-label="Loading categories">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <div className="categories-page__skeleton" key={item} />
          ))}
        </div>
      ) : categories.length ? (
        <div className="categories-page__grid">
          {categories.map((category) => {
            const categoryUrl = `/shop?category=${encodeURIComponent(category.name)}`;
            const subCategories = category.subCategories || [];

            return (
              <section className="categories-page__card" key={category._id || category.name}>
                <Link className="categories-page__cover" to={categoryUrl} aria-label={`Browse ${category.name}`}>
                  <span className="categories-page__image-wrap">
                    <Layers3 className="categories-page__image-fallback" size={22} />
                    {category.image && (
                      <img
                        className="categories-page__image"
                        src={getImageUrl(category.image)}
                        alt={`${category.name} category`}
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </span>
                  <span className="categories-page__cover-shade" />
                  <span className="categories-page__cover-label">SHOP BY CATEGORY</span>
                  <span className="categories-page__cover-arrow"><ArrowUpRight size={19} /></span>
                </Link>
                <div className="categories-page__card-heading">
                  <div className="categories-page__card-title">
                    <h2>{category.name}</h2>
                    <p>{subCategories.length} {subCategories.length === 1 ? "collection" : "collections"}</p>
                  </div>
                  <Link
                    className="categories-page__view-all"
                    to={categoryUrl}
                    aria-label={`View all ${category.name} products`}
                    title={`View all ${category.name} products`}
                  >
                    <span>View all</span><ArrowRight size={15} />
                  </Link>
                </div>
                {subCategories.length ? (
                  <div className="categories-page__subcategories">
                    {subCategories.map((subCategory) => (
                      <Link
                        key={subCategory._id || subCategory.name}
                        className="categories-page__subcategory"
                        to={`${categoryUrl}&subCategory=${encodeURIComponent(subCategory.name)}`}
                      >
                        <span className="categories-page__subcategory-image-wrap">
                          <Layers3 className="categories-page__subcategory-fallback" size={14} />
                          {subCategory.image && (
                            <img
                              className="categories-page__subcategory-image"
                              src={getImageUrl(subCategory.image)}
                              alt=""
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          )}
                        </span>
                        <span>{subCategory.name}</span>
                        <ArrowRight size={13} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="categories-page__empty">Browse all products in this category.</p>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <span className="empty-state__icon">✳</span>
          <h2>No categories yet</h2>
          <p>New collections will appear here when they are available.</p>
        </div>
      )}
    </main>
  );
}
